/**
 * CoachNotesPanel — a generic notes feed (הערות מאמן/ת).
 *
 * Deliberately generic: it holds no menu-specific logic and does NO data
 * fetching of its own, so eating reports and measurements can reuse it as-is.
 * The parent owns the data and the persistence; this component only renders
 * the list and surfaces the saving state of the promises it gets back.
 *
 * Note shape: { id, authorId, authorName, content, createdAt }  (ISO string).
 * The content key is `content` — never `text`.
 *
 * Error codes: CK-10160 (add a note), CK-10161 (delete a note).
 */

import { useCallback, useMemo, useState } from "react";
import { ChatCircleDots, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

import { formatHebrewDate } from "../../../utils/dateUtils";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { useToast } from "../../../contexts/ToastContext";
import "./CoachNotesPanel.css";
import LoaderLoop from "../../shared/loader/LoaderLoop";

/** "היום 09:40" / "אתמול 18:02" / "12/09/2026 18:02" — Asia/Jerusalem. */
const formatNoteDate = (createdAt) => {
  if (!createdAt) return "";
  const dayKey = formatHebrewDate(createdAt, "yyyy-MM-dd");
  if (!dayKey) return "";

  const time = formatHebrewDate(createdAt, "HH:mm");
  const today = formatHebrewDate(new Date(), "yyyy-MM-dd");
  const yesterday = formatHebrewDate(
    new Date(Date.now() - 24 * 60 * 60 * 1000),
    "yyyy-MM-dd"
  );

  if (dayKey === today) return `היום ${time}`;
  if (dayKey === yesterday) return `אתמול ${time}`;
  return `${formatHebrewDate(createdAt, "dd/MM/yyyy")} ${time}`;
};

/**
 * @param {Array} notes  [{ id, authorId, authorName, content, createdAt }]
 * @param {function} onAdd (content) => Promise
 * @param {function} onDelete (noteId) => Promise
 * @param {string} title
 */
const CoachNotesPanel = ({
  notes = [],
  onAdd,
  onDelete,
  title = "הערות מאמן/ת",
}) => {
  const { showError } = useToast();

  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /** Newest first — the caller's order is never trusted. */
  const orderedNotes = useMemo(
    () =>
      [...(notes || [])].sort((a, b) =>
        String(b?.createdAt || "").localeCompare(String(a?.createdAt || ""))
      ),
    [notes]
  );

  const handleAdd = useCallback(async () => {
    const content = draft.trim();
    if (!content || isSaving || !onAdd) return;

    setIsSaving(true);
    try {
      await onAdd(content);
      setDraft("");
    } catch (error) {
      console.error("[CK-10160] Error adding a coach note:", {
        errorCode: "CK-10160",
        errorMessage: error?.message || error?.data?.message || "unknown",
        contentLength: content.length,
        timestamp: new Date().toISOString(),
      });
      showError("הוספת ההערה נכשלה, נסו שוב");
    } finally {
      setIsSaving(false);
    }
  }, [draft, isSaving, onAdd, showError]);

  const handleConfirmDelete = useCallback(async () => {
    if (!noteToDelete || !onDelete) return;
    const noteId = noteToDelete.id;

    setDeletingId(noteId);
    try {
      await onDelete(noteId);
      setNoteToDelete(null);
    } catch (error) {
      console.error("[CK-10161] Error deleting a coach note:", {
        errorCode: "CK-10161",
        errorMessage: error?.message || error?.data?.message || "unknown",
        noteId,
        timestamp: new Date().toISOString(),
      });
      showError("מחיקת ההערה נכשלה, נסו שוב");
    } finally {
      setDeletingId(null);
    }
  }, [noteToDelete, onDelete, showError]);

  return (
    <section className="ncnp-panel" aria-label={title}>
      <div className="ncnp-head">
        <span className="ncnp-head-icon">
          <ChatCircleDots size={20} weight="fill" />
        </span>
        <div className="ncnp-head-text">
          <h3>{title}</h3>
          <span className="ncnp-head-meta">{orderedNotes.length} הערות</span>
        </div>
      </div>

      {/* Composer */}
      <div className="ncnp-composer">
        <textarea
          className="ncnp-textarea"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="כתבו הערה..."
          rows={3}
          aria-label="הערה חדשה"
          disabled={isSaving}
        />
        <button
          type="button"
          className="ncnp-add-btn"
          onClick={handleAdd}
          disabled={!draft.trim() || isSaving}
        >
          {isSaving ? (
            <>
              <LoaderLoop size="xs" />
              <span>שומר...</span>
            </>
          ) : (
            <>
              <PaperPlaneTilt size={14} weight="fill" />
              <span>הוסף הערה</span>
            </>
          )}
        </button>
      </div>

      {/* List */}
      {orderedNotes.length === 0 ? (
        <div className="ncnp-empty">
          <ChatCircleDots size={24} weight="fill" />
          <p>עדיין אין הערות</p>
          <p className="ncnp-empty-hint">
            ההערות נשמרות כאן ומרוכזות גם בכרטיס הלקוח
          </p>
        </div>
      ) : (
        <ul className="ncnp-list">
          {orderedNotes.map((note) => (
            <li className="ncnp-note" key={note.id}>
              <div className="ncnp-note-head">
                <span className="ncnp-note-author">
                  {note.authorName || "לא ידוע"}
                </span>
                <span className="ncnp-note-date">
                  {formatNoteDate(note.createdAt)}
                </span>
                {onDelete && (
                  <button
                    type="button"
                    className="ncnp-icon-btn danger"
                    onClick={() => setNoteToDelete(note)}
                    disabled={deletingId === note.id}
                    aria-label="מחיקת ההערה"
                    title="מחיקת ההערה"
                  >
                    <Trash size={14} weight="fill" />
                  </button>
                )}
              </div>
              <p className="ncnp-note-content">{note.content}</p>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת הערה"
        message="האם למחוק את ההערה? לא ניתן לשחזר אותה."
        confirmText="מחיקה"
        cancelText="ביטול"
        type="danger"
        icon="delete"
        confirmDisabled={!!deletingId}
      />
    </section>
  );
};

export default CoachNotesPanel;
